import { mainConnection } from '../socket';
import { endpointMatch } from '../util';
import {
  registryEditorStore,
  RegistryKeyTreeItem,
} from '../store/registry-editor-store';
import { messageActions } from '../action';
import { dialogEvents } from '../component';
import { RegistrySearchFlags } from '../enums';

class RegistryEditorApi {
  cancelSearch() {
    return mainConnection.send('CancelRegistrySearch');
  }

  createNewKey(parentKeyPath: string, newKeyString: string): Promise<any> {
    if (!parentKeyPath) {
      messageActions.msg('info', 'Select a parent key first.');
      return;
    }
    if (!newKeyString) {
      messageActions.msg('info', 'New key name required.');
      return;
    }

    return mainConnection.sendAsync(
      'CreateNewRegistryKey',
      parentKeyPath,
      newKeyString,
      (response: PacketHandlerResponse<RegistryKeyNode>) => {
        if (response.hadErrors) {
          handleErrorResponse(response);
          return;
        }

        registryEditorStore.addSubKey(parentKeyPath, response.responseData);
      },
    );
  }

  createRegistryValue(
    parentKeyPath: string,
    valueName: string,
    valueData: string,
    kind: RegistryValueKind,
  ): Promise<any> {
    if (!parentKeyPath) {
      messageActions.msg('info', 'Select a parent key first.');
      return;
    }
    if (!valueName && kind != 'String') {
      messageActions.msg('info', 'Default value must be a String kind.');
      return;
    }

    return mainConnection.sendAsync(
      'CreateRegistryValue',
      parentKeyPath,
      valueName,
      valueData,
      kind,
      (response: PacketHandlerResponse<RegistryValue>) => {
        if (response.hadErrors) {
          handleErrorResponse(response);
          return;
        }

        this.getValues(parentKeyPath);
      },
    );
  }

  deleteKey(selectedNode: RegistryKeyTreeItem) {
    return new Promise((resolve, reject) => {
      mainConnection
        .sendAsync(
          'DeleteRegistryKey',
          selectedNode.fullKeyName,
          (response: PacketHandlerResponse<boolean>) => {
            if (response.hadErrors) {
              handleErrorResponse(response);
              reject();
              return;
            }

            registryEditorStore.setSelectedNode(selectedNode.parentKeyNode);

            this.getSubkeys(selectedNode.parentKeyNode.fullKeyName)
              .then(() => resolve())
              .catch(reason => reject(reason));
          },
        )
        .catch(reason => reject(reason));
    });
  }

  deleteValue(registryValue: RegistryValue) {
    return new Promise((resolve, reject) => {
      mainConnection
        .sendAsync(
          'DeleteRegistryValue',
          registryValue.keyName,
          registryValue.name,
          (response: PacketHandlerResponse<boolean>) => {
            if (response.hadErrors) {
              handleErrorResponse(response);
              reject();
              return;
            }

            registryEditorStore.setSelectedValue(null);

            this.getValues(registryValue.keyName)
              .then(() => resolve())
              .catch(reason => reject(reason));
          },
        )
        .catch(reason => reject(reason));
    });
  }

  getKeyTree(fullKeyPath: string): Promise<any> {
    return new Promise<any>((resolve, reject) => {
      mainConnection
        .sendAsync(
          'GetRegistryKeyTree',
          fullKeyPath,
          (response: PacketHandlerResponse<RegistryKeyNode>) => {
            if (response.hadErrors) {
              handleErrorResponse(response);
              reject();
              return;
            }

            registryEditorStore.addKeyTree(response.responseData);
            resolve();
          },
        )
        .catch(reason => reject(reason));
    });
  }

  getRootKeys(): Promise<any> {
    return mainConnection.sendAsync(
      'GetRootRegistryKeys',
      (response: PacketHandlerResponse<Array<RegistryKeyNode>>) => {
        if (response.hadErrors) {
          handleErrorResponse(response);
          return;
        }
        registryEditorStore.addRootKeys(response.responseData);
      },
    );
  }

  getSubkeys(parentKeyPath: string): Promise<any> {
    return mainConnection.sendAsync(
      'GetRegistrySubKeys',
      parentKeyPath,
      (response: PacketHandlerResponse<Array<RegistryKeyNode>>) => {
        if (response.hadErrors) {
          handleErrorResponse(response);
          return;
        }
        registryEditorStore.addSubKeys(parentKeyPath, response.responseData);
      },
    );
  }

  getValues(fullKeyName: string): Promise<any> {
    return mainConnection.sendAsync(
      'GetRegistryValues',
      fullKeyName,
      (response: PacketHandlerResponse<RegistryKeyNode>) => {
        if (response.hadErrors) {
          handleErrorResponse(response);
          return;
        }

        registryEditorStore.setKeyValues(response.responseData);
      },
    );
  }

  modifyRegistryValue(
    keyName: string,
    valueName: string,
    newValueData: string,
    kind: RegistryValueKind,
  ) {
    return new Promise((resolve, reject) => {
      mainConnection
        .sendAsync(
          'ModifyRegistryValue',
          keyName,
          valueName,
          newValueData,
          kind,
          (response: PacketHandlerResponse<boolean>) => {
            if (response.hadErrors) {
              handleErrorResponse(response);
              reject();
              return;
            }

            registryEditorStore.setSelectedValue(null);

            this.getValues(keyName)
              .then(() => resolve())
              .catch(reason => reject(reason));
          },
        )
        .catch(reason => reject(reason));
    });
  }

  renameRegistryKey(
    registryKeyItem: RegistryKeyTreeItem,
    newKeyName: string,
  ): Promise<any> {
    var parentKey = registryKeyItem.parentKeyNode.fullKeyName;

    return new Promise((resolve, reject) => {
      mainConnection
        .sendAsync(
          'RenameRegistryKey',
          registryKeyItem.fullKeyName,
          newKeyName,
          (response: PacketHandlerResponse<boolean>) => {
            if (response.hadErrors) {
              handleErrorResponse(response);
              reject();
              return;
            }

            this.getSubkeys(parentKey)
              .then(() => {
                var newNode =
                  registryEditorStore.registryKeys[
                    `${parentKey}\\${newKeyName}`
                  ];
                registryEditorStore.setSelectedNode(newNode);
                resolve();
              })
              .catch(reason => reject(reason));
          },
        )
        .catch(reason => reject(reason));
    });
  }

  renameRegistryValue(
    fullKeyName: string,
    oldValueName: string,
    newValueName: string,
  ): Promise<any> {
    return new Promise((resolve, reject) => {
      mainConnection
        .sendAsync(
          'RenameRegistryValue',
          fullKeyName,
          oldValueName,
          newValueName,
          (response: PacketHandlerResponse<boolean>) => {
            if (response.hadErrors) {
              handleErrorResponse(response);
              reject();
              return;
            }

            this.getValues(fullKeyName)
              .then(() => resolve())
              .catch(reason => reject(reason));
          },
        )
        .catch(reason => reject(reason));
    });
  }

  searchRegistry(
    fullKeyName: string,
    searchString: string,
    searchFlags: RegistrySearchFlags,
  ) {
    return mainConnection.send('StartRegistrySearch', [
      fullKeyName,
      searchString,
      searchFlags,
    ]);
  }
}

mainConnection.listenKeys(endpointMatch, {
  registrySearchMatch(match: RegistrySearchMatch) {
    registryEditorStore.addSearchMatch(match);
  },
  startRegistrySearch(response: PacketHandlerResponse<RegistrySearchResult>) {
    if (response.hadErrors) {
      handleErrorResponse(response);
      registryEditorStore.setSearchStatus('Error');
      return;
    }
    messageActions.msg('info', response.responseData.message);
    registryEditorStore.setSearchStatus(response.responseData.status);
  },
});

export const registryEditorApi = new RegistryEditorApi();

function handleErrorResponse<T>(response: PacketHandlerResponse<T>) {
  if (response.toastMessage) {
    messageActions.msg('warning', response.toastMessage);
  }

  if (response.alertMessage) {
    dialogEvents.dialog({
      body: response.alertMessage,
      title: 'Response Error',
    });
  }
}
